/**
 * data/repos/copilotRepo — repositório da área `copilot` (TASK-21, Req 9, DD-004).
 *
 * Responsabilidade: enviar POST /v1/copilot/chat e devolver a Response crua.
 * O caller (useCopilotChat) interpreta a resposta como SSE ou JSON.
 *
 * Não existe mock de SSE; a área `copilot` é sempre real (consta em VITE_API_REAL_AREAS
 * por padrão conforme docker-compose do projeto). O gateway retornará 503 enquanto o
 * provedor de IA não estiver configurado — esse comportamento é o caminho nominal para
 * TASK-21 (estado `degraded`, Req 9.4).
 *
 * Headers enviados conforme contrato do design.md §20.15:
 * - Authorization: Bearer <token>
 * - X-Azim-Tenant: <tenantId>          (Req 9.5, Req 9.6)
 * - X-Azim-Business-Unit: <buId>       (Req 9.5, Req 9.6)
 *
 * Referências: design.md §20.15, Req 9, Req 9.5, Req 9.6, TASK-21.
 */

import { generateCorrelationId } from "../../adapters/http/correlation.js";

const BASE_URL =
  (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "/v1";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

/** Payload enviado ao endpoint /v1/copilot/chat (design.md §20.15). */
export interface ChatPayload {
  readonly message: string;
  readonly context: {
    readonly tenantId: string | null;
    readonly businessUnitId: string | null;
  };
}

// ---------------------------------------------------------------------------
// postCopilotChat
// ---------------------------------------------------------------------------

/**
 * Envia uma mensagem para o copiloto e retorna a Response crua (sem parse).
 *
 * O header `Accept: text/event-stream, application/json` indica preferência por SSE;
 * o gateway escolhe conforme suporte. O caller decide como consumir a resposta.
 *
 * @param payload        - Corpo { message, context: { tenantId, businessUnitId } }.
 * @param authToken      - Bearer token da sessão autenticada.
 * @param tenantId       - GUID do tenant (X-Azim-Tenant).
 * @param businessUnitId - GUID da BU ativa (X-Azim-Business-Unit).
 * @param signal         - AbortSignal para cancelar em andamento.
 */
export async function postCopilotChat(
  payload: ChatPayload,
  authToken: string | null,
  tenantId: string | null,
  businessUnitId: string | null,
  signal?: AbortSignal,
): Promise<Response> {
  const url = `${BASE_URL}/copilot/chat`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    // Preferência por SSE; fallback para JSON (design.md §20.15 — protocolo).
    Accept: "text/event-stream, application/json",
    // LDG-0195: sem isto, um erro visto no navegador não tem como ser juntado ao rastro do
    // servidor (o CorrelationIdMiddleware do gateway/serviços gera um id próprio quando o
    // header não vem — a requisição não fica sem correlação, só sem JUNÇÃO com o cliente).
    // Não migramos postCopilotChat para o ApiClient para ganhar isso de graça: o ApiClient
    // lança em qualquer resposta não-ok (o 503 do copiloto é o caminho NOMINAL do estado
    // `degraded`), fixa Accept: "application/json" e injeta X-Tenant-Slug, que este contrato
    // não prevê — as três incompatibilidades pesam mais que reusar o gerador de id sozinho.
    "X-Correlation-Id": generateCorrelationId(),
  };

  if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
  if (tenantId) headers["X-Azim-Tenant"] = tenantId;
  if (businessUnitId) headers["X-Azim-Business-Unit"] = businessUnitId;

  return fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
    ...(signal ? { signal } : {}),
  });
}
